import Vue from 'vue'

/**
 * 水印配置项（兼容 Element Plus 风格）
 * @typedef {Object} WatermarkOptions
 * @property {string|string[]} content - 水印文本内容，支持字符串或字符串数组 数组的话会换行展示
 * @property {number} [rotate=-22] - 旋转角度，默认 -22
 * @property {Object} [font] - 字体样式配置
 * @property {string} [font.color='rgba(0,0,0,.15)'] - 字体颜色
 * @property {number} [font.fontSize=16] - 字体大小
 * @property {string} [font.fontWeight='normal'] - 字体粗细
 * @property {string} [font.fontFamily='sans-serif'] - 字体族
 * @property {string} [font.fontStyle='normal'] - 字体样式
 * @property {string} [font.textAlign='center'] - 文本对齐方式
 * @property {string} [font.textBaseline='middle'] - 文本基线
 * @property {number|number[]} [gap=[100,100]] - 水印之间的间距，支持数字或数组 [x, y]
 * @property {number|number[]} [offset=[0,0]] - 水印从容器左上角的偏移，支持数字或数组 [x, y]
 */

// 水印图片缓存，key 为配置的字符串化，value 为 base64 图片
// 配置只在登录时改变，切换账号时自动清除
const watermarkCache = new Map()

// CSS 类名缓存，相同配置使用相同的类名（多个容器共享同一个 CSS 类）
const watermarkClassCache = new Map()

// 记录当前使用的配置 key，用于检测配置变化
let currentConfigKey = null
// 记录当前 store 配置的 key，用于检测 store 配置是否变化
let currentStoreConfigKey = null

// 全局 watcher 相关
let globalWatcher = null
let globalStore = null
// 存储所有水印元素的信息：WeakMap<HTMLElement, {vm, localOptions}>
const watermarkElements = new WeakMap()
// 使用 Set 存储元素引用（用于遍历，配合 WeakMap）
const watermarkElementRefs = new Set()

// 默认配置
const defaultOptions = {
	content: '',
	rotate: -22,
	font: {
		color: 'rgba(153,153,153,.24)',
		fontSize: 14,
		fontWeight: 'normal',
		fontFamily: 'sans-serif',
		fontStyle: 'normal',
		textAlign: 'center',
		textBaseline: 'middle'
	},
	gap: [100, 100],
	offset: [0, 0]
}

/**
 * 生成配置的唯一 key
 * @param {WatermarkOptions} options
 * @returns {string}
 */
function generateCacheKey(options) {
	return JSON.stringify(options)
}

/**
 * 创建水印图片（Canvas）
 * @param {WatermarkOptions} options
 * @returns {string} base64 图片
 */
function createWatermarkImage(options) {
	const cacheKey = generateCacheKey(options)

	// 检查缓存（配置都一样时，多个容器共享同一个图片）
	if (watermarkCache.has(cacheKey)) {
		return watermarkCache.get(cacheKey)
	}

	const {
		content,
		rotate = defaultOptions.rotate,
		font = {},
		gap = defaultOptions.gap,
		offset = defaultOptions.offset
	} = options

	// 合并字体配置
	const fontConfig = {
		...defaultOptions.font,
		...font
	}

	// 处理 gap 和 offset，支持数字或数组
	const gapX = Array.isArray(gap) ? gap[0] : gap
	const gapY = Array.isArray(gap) ? gap[1] : gap
	const offsetX = Array.isArray(offset) ? offset[0] : offset
	const offsetY = Array.isArray(offset) ? offset[1] : offset

	// 处理 content，支持字符串或数组
	const contentArray = Array.isArray(content) ? content : [content]

	// 创建离屏 canvas
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')

	// 设置字体样式
	const fontSize = fontConfig.fontSize
	const fontFamily = fontConfig.fontFamily
	const fontWeight = fontConfig.fontWeight
	const fontStyle = fontConfig.fontStyle
	ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`
	ctx.fillStyle = fontConfig.color
	ctx.textAlign = fontConfig.textAlign
	ctx.textBaseline = fontConfig.textBaseline

	// 测量文本宽度和高度
	let maxWidth = 0
	const lineHeight = fontSize * 1.2
	contentArray.forEach(text => {
		const metrics = ctx.measureText(text)
		maxWidth = Math.max(maxWidth, metrics.width)
	})

	const textHeight = contentArray.length * lineHeight
	const textWidth = maxWidth

	// 计算画布大小（考虑旋转）
	const angle = (rotate * Math.PI) / 180
	const cos = Math.abs(Math.cos(angle))
	const sin = Math.abs(Math.sin(angle))

	// 计算旋转后的文本尺寸（包围盒）
	const rotatedTextWidth = textWidth * cos + textHeight * sin
	const rotatedTextHeight = textWidth * sin + textHeight * cos

	// 画布大小 = 旋转后的文本尺寸 + gap（gap 作为右边和下方的间距）
	const canvasWidth = rotatedTextWidth + gapX
	const canvasHeight = rotatedTextHeight + gapY

	canvas.width = canvasWidth
	canvas.height = canvasHeight

	// 重新设置字体（canvas resize 会重置 context）
	ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`
	ctx.fillStyle = fontConfig.color
	ctx.textAlign = fontConfig.textAlign
	ctx.textBaseline = fontConfig.textBaseline

	// 计算旋转后文本的左上角在画布中的位置
	// 旋转后文本的包围盒左上角位置 = offset
	// 旋转中心（文本中心）在旋转后包围盒中的位置
	// 由于旋转，文本中心在旋转后包围盒中的位置需要计算
	// 简化处理：假设旋转后文本中心在包围盒中心（对于大多数角度这是近似正确的）
	const rotateCenterX = offsetX + rotatedTextWidth / 2
	const rotateCenterY = offsetY + rotatedTextHeight / 2

	// 移动到旋转中心，然后旋转
	ctx.save()
	ctx.translate(rotateCenterX, rotateCenterY)
	ctx.rotate(angle)

	// 绘制文本（文本中心在原点，textAlign 为 center，所以 x=0）
	contentArray.forEach((text, index) => {
		const y = (index - (contentArray.length - 1) / 2) * lineHeight
		ctx.fillText(text, 0, y)
	})

	ctx.restore()

	// 转换为 base64
	const base64 = canvas.toDataURL('image/png')

	// 缓存结果（配置都一样时，多个容器共享）
	watermarkCache.set(cacheKey, base64)

	return base64
}

/**
 * 创建或获取 CSS 类（相同配置时，多个容器共享同一个 CSS 类）
 * @param {string} base64Image
 * @returns {string} CSS 类名
 */
function getOrCreateWatermarkClass(base64Image) {
	if (watermarkClassCache.has(base64Image)) {
		return watermarkClassCache.get(base64Image)
	}

	const className = `watermark-bg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
	const style = document.createElement('style')
	style.textContent = `
		.${className} {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			pointer-events: none;
			z-index: 9999;
			background-image: url(${base64Image});
			background-repeat: repeat;
			background-position: 0 0;
		}
	`
	document.head.appendChild(style)
	watermarkClassCache.set(base64Image, className)
	return className
}

/**
 * 清理水印
 * @param {HTMLElement} el
 */
function removeWatermark(el) {
	el.querySelectorAll('.watermark-container').forEach((node) => node.remove())

	// 从全局集合中移除
	watermarkElements.delete(el)
	watermarkElementRefs.delete(el)

	// 清理配置 key
	delete el._watermarkConfigKey
}

/**
 * 创建水印容器（简化版，配置不变时不需要 MutationObserver）
 * @param {HTMLElement} el
 * @param {WatermarkOptions} options
 * @param {string} configKey - 配置的唯一 key
 */
function createWatermarkElement(el, options, configKey) {
	const existing = el.querySelectorAll('.watermark-container')
	// 配置未变且仅有一个容器时跳过，避免无意义重绘
	if (el._watermarkConfigKey === configKey && existing.length === 1) {
		return
	}
	// 避免出现多个 .watermark-container（只删第一个会叠加水印）
	existing.forEach((node) => node.remove())

	// 创建水印容器
	const watermarkDiv = document.createElement('div')
	watermarkDiv.className = 'watermark-container'

	// 生成水印图片（相同配置会使用缓存）
	const base64Image = createWatermarkImage(options)

	// 使用 CSS 类（相同配置时，多个容器共享同一个 CSS 类，性能更好）
	const className = getOrCreateWatermarkClass(base64Image)
	watermarkDiv.className = `watermark-container ${className}`

	// 确保父元素有相对定位
	const originalPosition = window.getComputedStyle(el).position
	if (originalPosition === 'static') {
		el.style.position = 'relative'
	}

	// 添加到元素
	el.appendChild(watermarkDiv)

	// 保存当前配置 key
	el._watermarkConfigKey = configKey
}

/**
 * 获取合并后的水印配置
 * @param {Object} vm - Vue 实例
 * @param {Object} localOptions - 局部配置（指令传入的）
 * @returns {WatermarkOptions|null}
 */
function getMergedWatermarkOptions(vm, localOptions = {}) {
	if (!vm || !vm.$store) {
		return null
	}

	const meetingDetail = vm.$store.state.meetingDetail || {}

	// 检查是否启用水印
	if (!meetingDetail.is_open_global_watermark) {
		// 如果之前有配置，现在关闭了，清除缓存（切换账号或关闭水印）
		if (currentConfigKey !== null) {
			watermarkCache.clear()
			watermarkClassCache.clear()
			currentConfigKey = null
			currentStoreConfigKey = null
		}
		return null
	}

	const storeOptions = meetingDetail.watermarkOptions || {}

	// 合并配置：默认配置 -> store配置 -> 局部配置
	const mergedOptions = {
		...defaultOptions,
		...storeOptions,
		...localOptions,
		// 深度合并 font 对象
		font: {
			...defaultOptions.font,
			...(storeOptions.font || {}),
			...(localOptions.font || {})
		}
	}

	// 确保 content 存在
	if (!mergedOptions.content) {
		return null
	}

	// 检测配置是否变化（切换账号时配置会变化）
	const newConfigKey = generateCacheKey(mergedOptions)
	if (currentConfigKey !== null && currentConfigKey !== newConfigKey) {
		// 配置变化了，清除旧缓存
		watermarkCache.clear()
		watermarkClassCache.clear()
	}
	currentConfigKey = newConfigKey

	return mergedOptions
}

/**
 * 更新所有水印元素
 */
function updateAllWatermarks() {
	if (!globalStore) {
		return
	}

	// 遍历所有注册的水印元素

	watermarkElementRefs.forEach(el => {
		// 检查元素是否还在 DOM 中
		if (!document.contains(el)) {
			watermarkElementRefs.delete(el)
			watermarkElements.delete(el)
			return
		}

		const elementInfo = watermarkElements.get(el)
		if (!elementInfo) {
			watermarkElementRefs.delete(el)
			return
		}

		const { vm, localOptions, shouldShow = true } = elementInfo

		// 如果不应该显示水印，移除水印
		if (!shouldShow) {

			removeWatermark(el)
			return
		}

		// 获取合并后的配置
		const options = getMergedWatermarkOptions(vm, localOptions)


		if (options) {
			const configKey = generateCacheKey(options)
			createWatermarkElement(el, options, configKey)
		} else {

			removeWatermark(el)
		}
	})
}

/**
 * 初始化全局 watcher（只创建一次）
 * @param {Object} store - Vuex store 实例
 */
function initGlobalWatcher(store) {
	if (globalWatcher || !store) {
		return
	}

	globalStore = store

	// 创建全局 watcher，只监听一次
	globalWatcher = store.watch(
		(state) => {
			const meetingDetail = state.meetingDetail || {}
			return {
				is_open_global_watermark: meetingDetail.is_open_global_watermark,
				watermarkOptions: meetingDetail.watermarkOptions
			}
		},
		() => {
			// 检查 store 配置是否真的变化了
			const meetingDetail = globalStore.state.meetingDetail || {}

			// 检查是否启用水印
			if (!meetingDetail.is_open_global_watermark) {
				// 如果之前有配置，现在关闭了，清除缓存并更新
				if (currentStoreConfigKey !== null) {
					currentStoreConfigKey = null
					currentConfigKey = null
					watermarkCache.clear()
					watermarkClassCache.clear()
					updateAllWatermarks()
				}
				return
			}

			// 获取 store 配置（不包含 localOptions）
			const storeOptions = meetingDetail.watermarkOptions || {}
			const storeMergedOptions = {
				...defaultOptions,
				...storeOptions,
				font: {
					...defaultOptions.font,
					...(storeOptions.font || {})
				}
			}

			// 生成 store 配置的 key
			const newStoreConfigKey = generateCacheKey(storeMergedOptions)

			// 如果 store 配置没变化，不执行更新
			if (currentStoreConfigKey === newStoreConfigKey) {
				return
			}

			// store 配置变化了，更新并执行更新
			currentStoreConfigKey = newStoreConfigKey

			// store 变化时，更新所有水印元素
			updateAllWatermarks()
		},
		{ deep: true }
	)
}

// Vue 指令
const directiveName = 'watermark'

export default {
	install() {
		Vue.directive(directiveName, {
			inserted(el, binding, vnode) {
				const vm = vnode.context

				// 解析 binding.value：
				// - 如果是布尔值，作为是否显示水印的控制（true=显示，false=不显示）
				// - 如果是对象，作为局部配置选项
				// - 如果未定义，默认为 true（显示水印）
				let shouldShow = true
				let localOptions = {}

				if (typeof binding.value === 'boolean') {
					shouldShow = binding.value
				} else if (binding.value && typeof binding.value === 'object') {
					localOptions = binding.value
					shouldShow = true // 传入对象时默认显示
				} else if (binding.value === undefined || binding.value === null) {
					shouldShow = true // 默认显示
				}

				// 如果不应该显示水印，直接返回
				if (!shouldShow) {
					return
				}

				// 初始化全局 watcher（如果还没有）
				if (vm && vm.$store && !globalWatcher) {
					initGlobalWatcher(vm.$store)
				}

				// 获取合并后的配置
				const options = getMergedWatermarkOptions(vm, localOptions)

				if (!options) {
					return
				}

				const configKey = generateCacheKey(options)
				createWatermarkElement(el, options, configKey)

				// 注册到全局集合中
				watermarkElements.set(el, { vm, localOptions, shouldShow })
				watermarkElementRefs.add(el)
			},

			update(el, binding, vnode) {
				const vm = vnode.context

				// 解析 binding.value
				let shouldShow = true
				let localOptions = {}

				if (typeof binding.value === 'boolean') {
					shouldShow = binding.value
				} else if (binding.value && typeof binding.value === 'object') {
					localOptions = binding.value
					shouldShow = true
				} else if (binding.value === undefined || binding.value === null) {
					shouldShow = true
				}

				// 更新元素信息
				watermarkElements.set(el, { vm, localOptions, shouldShow })

				// 如果不应该显示水印，移除水印
				if (!shouldShow) {

					removeWatermark(el)
					return
				}

				// 获取合并后的配置
				const options = getMergedWatermarkOptions(vm, localOptions)

				if (options) {
					const configKey = generateCacheKey(options)
					createWatermarkElement(el, options, configKey)
				} else {

					removeWatermark(el)
				}
			},

			unbind(el) {

				removeWatermark(el)
				watermarkElementRefs.delete(el)
			}
		})
	}
}

/**
 * 清除水印缓存（切换账号时调用）
 */
export function clearWatermarkCache() {
	watermarkCache.clear()
	watermarkClassCache.clear()
}



// WEBPACK FOOTER //
// ./src/directive/watermark.js